/**
 * AddPaymentDialog Component
 * Dialog for adding a new payment method
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  useTheme,
  MenuItem
} from '@mui/material';
import { X } from '@phosphor-icons/react';

interface AddPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (paymentData: PaymentMethodData) => void;
}

export interface PaymentMethodData {
  billingEmail: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  country: string;
  city: string;
  zipCode: string;
  state: string;
  einNumber: string;
  fullName: string;
  cardNumber: string;
  expirationDate: string;
  cvv: string;
}

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

// Shared input styling for Attio-style inputs
const getInputStyles = (isDark: boolean) => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '14px',
    borderRadius: '8px',
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : '#fff',
    '& input': {
      py: 1,
      px: 1.5
    },
    '& fieldset': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      borderWidth: '1px'
    },
    '&:hover fieldset': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
    },
    '&.Mui-focused fieldset': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      borderWidth: '1px'
    }
  }
});

// Shared label styling
const getLabelStyles = (isDark: boolean) => ({
  mb: 0.75,
  fontSize: '13px',
  fontWeight: 400,
  color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)'
});

export const AddPaymentDialog: React.FC<AddPaymentDialogProps> = ({
  open,
  onClose,
  onSubmit
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [formData, setFormData] = useState<PaymentMethodData>({
    billingEmail: '',
    company: '',
    addressLine1: '',
    addressLine2: '',
    country: 'United States of America',
    city: '',
    zipCode: '',
    state: '',
    einNumber: '',
    fullName: '',
    cardNumber: '',
    expirationDate: '',
    cvv: ''
  });

  const handleChange = (field: keyof PaymentMethodData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmit = () => {
    onSubmit(formData);
    onClose();
  };

  const inputSx = getInputStyles(isDark);
  const labelSx = getLabelStyles(isDark);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxWidth: '520px',
          boxShadow: isDark
            ? '0 24px 48px rgba(0, 0, 0, 0.4)'
            : '0 24px 48px rgba(0, 0, 0, 0.12)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : 'none'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          px: 3,
          pt: 2.5,
          borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '16px' }}>
          Add payment method
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            borderRadius: '8px',
            color: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
            }
          }}
        >
          <X size={18} weight="regular" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Billing Email */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Billing Email <Typography component="span" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' }}>(required)</Typography>
            </Typography>
            <TextField
              fullWidth
              placeholder="billing@example.com"
              value={formData.billingEmail}
              onChange={handleChange('billingEmail')}
              sx={inputSx}
            />
          </Box>

          {/* Company */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Company <Typography component="span" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' }}>(required)</Typography>
            </Typography>
            <TextField
              fullWidth
              placeholder="Example Company"
              value={formData.company}
              onChange={handleChange('company')}
              sx={inputSx}
            />
          </Box>

          {/* Address Line 1 */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Address Line 1 <Typography component="span" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' }}>(required)</Typography>
            </Typography>
            <TextField
              fullWidth
              placeholder="123 Main Street"
              value={formData.addressLine1}
              onChange={handleChange('addressLine1')}
              sx={inputSx}
            />
          </Box>

          {/* Address Line 2 */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Address Line 2
            </Typography>
            <TextField
              fullWidth
              placeholder="Apartment, suite, etc."
              value={formData.addressLine2}
              onChange={handleChange('addressLine2')}
              sx={inputSx}
            />
          </Box>

          {/* Country and City */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={labelSx}>
                Country <Typography component="span" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' }}>(required)</Typography>
              </Typography>
              <TextField
                select
                fullWidth
                value={formData.country}
                onChange={handleChange('country')}
                sx={{
                  ...inputSx,
                  '& .MuiSelect-select': {
                    py: 1,
                    px: 1.5
                  }
                }}
              >
                <MenuItem value="United States of America" sx={{ fontSize: '14px' }}>
                  United States of America
                </MenuItem>
                <MenuItem value="Canada" sx={{ fontSize: '14px' }}>
                  Canada
                </MenuItem>
                <MenuItem value="United Kingdom" sx={{ fontSize: '14px' }}>
                  United Kingdom
                </MenuItem>
              </TextField>
            </Box>
            <Box>
              <Typography variant="body2" sx={labelSx}>
                City <Typography component="span" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' }}>(required)</Typography>
              </Typography>
              <TextField
                fullWidth
                placeholder="Springfield"
                value={formData.city}
                onChange={handleChange('city')}
                sx={inputSx}
              />
            </Box>
          </Box>

          {/* Zip, State, EIN */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '0.8fr 1.2fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={labelSx}>
                Zip Code <Typography component="span" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' }}>(required)</Typography>
              </Typography>
              <TextField
                fullWidth
                placeholder="12345"
                value={formData.zipCode}
                onChange={handleChange('zipCode')}
                sx={inputSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={labelSx}>
                State <Typography component="span" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' }}>(required)</Typography>
              </Typography>
              <TextField
                select
                fullWidth
                value={formData.state}
                onChange={handleChange('state')}
                sx={{
                  ...inputSx,
                  '& .MuiSelect-select': {
                    py: 1,
                    px: 1.5
                  }
                }}
              >
                <MenuItem value="" disabled sx={{ fontSize: '14px', color: 'text.secondary' }}>
                  Select...
                </MenuItem>
                {US_STATES.map((state) => (
                  <MenuItem key={state} value={state} sx={{ fontSize: '14px' }}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box>
              <Typography variant="body2" sx={labelSx}>
                EIN number
              </Typography>
              <TextField
                fullWidth
                placeholder="12-3456789"
                value={formData.einNumber}
                onChange={handleChange('einNumber')}
                sx={inputSx}
              />
            </Box>
          </Box>

          {/* Full Name */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Full name
            </Typography>
            <TextField
              fullWidth
              placeholder="John Doe"
              value={formData.fullName}
              onChange={handleChange('fullName')}
              sx={inputSx}
            />
          </Box>

          {/* Card Number */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Card
            </Typography>
            <TextField
              fullWidth
              placeholder="Card number"
              value={formData.cardNumber}
              onChange={handleChange('cardNumber')}
              sx={inputSx}
            />
          </Box>

          {/* Expiration & CVV */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 0.6fr', gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={labelSx}>
                Expiration Date
              </Typography>
              <TextField
                fullWidth
                placeholder="MM / YY"
                value={formData.expirationDate}
                onChange={handleChange('expirationDate')}
                sx={inputSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={labelSx}>
                CVV
              </Typography>
              <TextField
                fullWidth
                placeholder="123"
                value={formData.cvv}
                onChange={handleChange('cvv')}
                sx={inputSx}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 500,
            color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
            py: 0.625,
            px: 1.5,
            borderRadius: '6px',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
            }
          }}
        >
          Cancel
          <Typography
            component="span"
            sx={{
              ml: 0.75,
              fontSize: '10px',
              color: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.35)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
              borderRadius: '3px',
              px: 0.5,
              py: 0.125
            }}
          >
            ESC
          </Typography>
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 600,
            borderRadius: '6px',
            py: 0.625,
            px: 2,
            backgroundColor: isDark ? '#fff' : '#1A1A1A',
            color: isDark ? '#000' : '#fff',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.9)' : '#000',
              boxShadow: 'none'
            }
          }}
        >
          Add payment method
          <Typography
            component="span"
            sx={{
              ml: 0.75,
              fontSize: '12px',
              opacity: 0.7
            }}
          >
            ↵
          </Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
